"use client"

import * as React from "react"
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from "embla-carousel-react"
import { ArrowLeft, ArrowRight } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { ArrowRightThin } from "@/components/icons/ArrowRightThin"

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

type CarouselProps = {
  opts?: CarouselOptions
  plugins?: CarouselPlugin
  orientation?: "horizontal" | "vertical"
  setApi?: (api: CarouselApi) => void
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  api: ReturnType<typeof useEmblaCarousel>[1]
  scrollPrev: () => void
  scrollNext: () => void
  canScrollPrev: boolean
  canScrollNext: boolean
} & CarouselProps

const CarouselContext = React.createContext<CarouselContextProps | null>(null)

/**
 * Carousel-Context lesen. Liefert `null`, wenn keiner vorhanden ist.
 *
 * Warf frueher stattdessen. Das ist als Schutz gegen Fehlverschachtelung
 * gedacht, riss aber den Build: Article.tsx rendert eingebettete Bloeke mit
 * renderToStaticMarkup (siehe dortigen resolver), und dieser isolierte
 * Renderdurchlauf fuehrt keine useEffect-Hooks aus. Embla initialisiert sich
 * nie, der Context bleibt leer, und der Wurf riss die ganze Seite mit —
 * `/blog/selbsthilfegruppen-coaching-mentoring-diabetes-typ-1` liess sich
 * deshalb nicht prerendern.
 *
 * Statt zu werfen warnen wir und rendern degradiert weiter: Inhalte bleiben
 * sichtbar, nur die Navigation entfaellt. Eine UI-Komponente soll einen Build
 * nicht kippen.
 */
function useCarousel(): CarouselContextProps | null {
  const context = React.useContext(CarouselContext)

  if (!context && process.env.NODE_ENV !== "production") {
    console.warn(
      "useCarousel: kein <Carousel>-Context. Navigation wird ausgelassen. " +
        "Bei renderToStaticMarkup ist das erwartbar, sonst fehlt der Wrapper."
    )
  }

  return context
}

const Carousel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & CarouselProps
>(
  (
    {
      orientation = "horizontal",
      opts,
      setApi,
      plugins,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === "horizontal" ? "x" : "y",
      },
      plugins
    )
    const [canScrollPrev, setCanScrollPrev] = React.useState(false)
    const [canScrollNext, setCanScrollNext] = React.useState(false)

    const onSelect = React.useCallback((api: CarouselApi) => {
      if (!api) {
        return
      }

      setCanScrollPrev(api.canScrollPrev())
      setCanScrollNext(api.canScrollNext())
    }, [])

    const scrollPrev = React.useCallback(() => {
      api?.scrollPrev()
    }, [api])

    const scrollNext = React.useCallback(() => {
      api?.scrollNext()
    }, [api])

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault()
          scrollPrev()
        } else if (event.key === "ArrowRight") {
          event.preventDefault()
          scrollNext()
        }
      },
      [scrollPrev, scrollNext]
    )

    React.useEffect(() => {
      if (!api || !setApi) {
        return
      }

      setApi(api)
    }, [api, setApi])

    React.useEffect(() => {
      if (!api) {
        return
      }

      onSelect(api)
      api.on("reInit", onSelect)
      api.on("select", onSelect)

      return () => {
        api?.off("select", onSelect)
      }
    }, [api, onSelect])

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation:
            orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
        }}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn("relative", className)}
          role="region"
          aria-roledescription="carousel"
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    )
  }
)
Carousel.displayName = "Carousel"

const CarouselContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  // Ohne Context weiterrendern: die Inhalte sollen sichtbar bleiben, nur eben
  // ohne Scroll-Mechanik.
  const ctx = useCarousel()
  const carouselRef = ctx?.carouselRef
  const orientation = ctx?.orientation ?? "horizontal"

  return (
    <div ref={carouselRef} className="overflow-hidden">
      <div
        ref={ref}
        className={cn(
          "flex",
          orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col",
          className
        )}
        {...props}
      />
    </div>
  )
})
CarouselContent.displayName = "CarouselContent"

const CarouselItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const orientation = useCarousel()?.orientation ?? "horizontal"

  return (
    <div
      ref={ref}
      role="group"
      aria-roledescription="slide"
      className={cn(
        "min-w-0 shrink-0 grow-0 basis-full",
        orientation === "horizontal" ? "pl-4" : "pt-4",
        className
      )}
      {...props}
    />
  )
})
CarouselItem.displayName = "CarouselItem"

const CarouselPrevious = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button>
>(({ className, variant = "outline", size = "icon", ...props }, ref) => {
  const ctx = useCarousel()
  // Ohne Scroll-Mechanik waere der Button funktionslos.
  if (!ctx) return null
  const { orientation, scrollPrev, canScrollPrev } = ctx

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      className={cn(
        "absolute  h-8 w-8 rounded-full",
        orientation === "horizontal"
          ? "-left-12 top-1/2 -translate-y-1/2"
          : "-top-12 left-1/2 -translate-x-1/2 rotate-90",
        className
      )}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <ArrowLeft className="h-4 w-4" />
      <span className="sr-only">Previous slide</span>
    </Button>
  )
})
CarouselPrevious.displayName = "CarouselPrevious"

const CarouselNext = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button>
>(({ className, variant = "outline", size = "icon", ...props }, ref) => {
  const ctx = useCarousel()
  if (!ctx) return null
  const { orientation, scrollNext, canScrollNext } = ctx

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      className={cn(
        "absolute h-8 w-8 rounded-full",
        orientation === "horizontal"
          ? "-right-12 top-1/2 -translate-y-1/2"
          : "-bottom-12 left-1/2 -translate-x-1/2 rotate-90",
        className
      )}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      <ArrowRight className="h-4 w-4" />
      <span className="sr-only">Next slide</span>
    </Button>
  )
})
CarouselNext.displayName = "CarouselNext"

// Custom navigation buttons with ArrowRightThin icon for galleries
const GalleryNavPrevious = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & { show?: boolean }
>(({ className, show = true, ...props }, ref) => {
  const ctx = useCarousel()

  if (!ctx || !show || !ctx.canScrollPrev) return null
  const { scrollPrev } = ctx

  return (
    <button
      ref={ref}
      className={cn(
        "absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-10",
        "hidden md:flex items-center justify-center",
        "w-10 h-10 rounded-full bg-white shadow-md",
        "hover:bg-black transition-colors group",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        className
      )}
      disabled={!ctx.canScrollPrev}
      onClick={scrollPrev}
      aria-label="Previous slide"
      {...props}
    >
      <ArrowRightThin className="h-5 w-5 rotate-180 group-hover:text-white transition-colors" />
    </button>
  )
})
GalleryNavPrevious.displayName = "GalleryNavPrevious"

const GalleryNavNext = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & { show?: boolean }
>(({ className, show = true, ...props }, ref) => {
  const ctx = useCarousel()

  if (!ctx || !show || !ctx.canScrollNext) return null
  const { scrollNext } = ctx

  return (
    <button
      ref={ref}
      className={cn(
        "absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-10",
        "hidden md:flex items-center justify-center",
        "w-10 h-10 rounded-full bg-white shadow-md",
        "hover:bg-black transition-colors group",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        className
      )}
      disabled={!ctx.canScrollNext}
      onClick={scrollNext}
      aria-label="Next slide"
      {...props}
    >
      <ArrowRightThin className="h-5 w-5 group-hover:text-white transition-colors" />
    </button>
  )
})
GalleryNavNext.displayName = "GalleryNavNext"

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  GalleryNavPrevious,
  GalleryNavNext,
}
